// @ts-check
// filters.js — "Custom filters" settings section.
//
// Extracted from settings-panel.js (Stage B of the monolith split). Renders
// the custom search-filter shortcut CRUD list and owns its own persistence
// helper (saveFilters), which validates + dedupes labels before writing to
// Settings and pushing the valid set to FilterShortcuts.
//
// renderFilters(ctx) receives the host primitives it shares with the other
// sections: ctx.row, ctx.rawRow, ctx.openExpansions, ctx.invalidate.

import { FilterShortcuts } from '../../notes/filter-shortcuts.js';
import type { SettingsSectionCtx } from '../settings-section-api.js';
import { Settings } from '../settings-store.js';
import { expandHeadingCta, makeP, removeButton } from './_common.js';

/**
 * @param {any} ctx - the section render context.
 * @returns {any[]} the section's rows.
 */
export function renderFilters(ctx: SettingsSectionCtx) {
  const rows = [];
  const ID = 'group:filters';
  // Filters are keyed by a stable `id`, not array index — index-as-key breaks a
  // retained editor when a filter above it is deleted. Backfill legacy entries
  // (created before ids) once.
  const raw = Settings?.get('filters', []) || [];
  let needsBackfill = false;
  const filters = raw.map((f: any) => {
    if (f.id) return { ...f };
    needsBackfill = true;
    return { ...f, id: crypto.randomUUID() };
  });
  // Fire-and-forget best-effort backfill: Settings.set() rejects on a write
  // failure (the store rolls back + toasts). Swallow so this un-awaited call
  // can't leak an unhandled promise rejection.
  if (needsBackfill && Settings) Settings.set('filters', filters).catch(() => {});

  rows.push(
    ctx.row({
      id: ID,
      label: 'Custom filters',
      // Right-hand action: an "Add filter" value button (like Encryption's
      // "Pair" / Calendars' "Add calendar") that expands the section and appends
      // a blank filter editor.
      control: () => buildAddHeadingCta(ctx, ID),
      expandable: true,
      searchExpand: true,
      cls: 'settings-row-group',
      searchText: `custom filters new add ${filters.map((f: any) => `${f.label} ${f.query}`).join(' ')}`,
    })
  );
  if (!ctx.openExpansions.has(ID)) return rows;

  // Empty + open: a faint hint, indented so it carries the group's accent line
  // (the affordance that collapses the group again — without a child row there
  // would be no line to click and the group couldn't be closed). The heading's
  // "Add filter" adds the first real filter.
  if (filters.length === 0) {
    rows.push(
      ctx.rawRow(() => makeP('No filters yet — use “Add filter” above.'), {
        key: 'filter-empty',
        indent: true,
        searchText: 'custom filters empty none',
      })
    );
    return rows;
  }

  // Each filter IS its editor — label + query edit inline, no intermediate
  // expandable row. Built ONCE and retained (the inputs are the source of
  // truth, so no `update` is needed). The handlers re-resolve the filter by
  // id, so a retained editor is immune to the list reindexing beneath it.
  for (const f of filters) {
    rows.push(
      ctx.rawRow(() => buildFilterEditor(ctx, f), {
        key: `filter-edit:${f.id}`,
        indent: true,
        cls: 'settings-row-editor',
        searchText: `custom filter ${f.label} ${f.query}`,
      })
    );
  }

  return rows;
}

// The Custom-filters heading's right-hand control: an "Add filter" value button
// (no caret — a direct action, like Encryption's "Pair" / Calendars' "Add
// calendar"). Clicking expands the group and appends a blank filter editor.
function buildAddHeadingCta(ctx: SettingsSectionCtx, groupId: string): HTMLElement {
  return expandHeadingCta(ctx, groupId, 'Add filter', {
    title: 'Add a custom filter',
    // Awaited before the re-render so the new blank filter row is present to edit.
    before: async () => {
      const cur = (Settings?.get('filters', []) || []).slice();
      await saveFilters([...cur, { id: crypto.randomUUID(), label: '', query: '' }]);
    },
  });
}

/** The label/query/delete editor for one filter — built once, id-keyed. */
function buildFilterEditor(ctx: SettingsSectionCtx, f: any): HTMLElement {
  const id = f.id;
  const editor = document.createElement('div');
  editor.className = 'settings-filter-editor';
  const labelInput = document.createElement('input');
  labelInput.type = 'text';
  labelInput.placeholder = 'Label';
  labelInput.value = f.label || '';
  labelInput.className = 'settings-mini-input';
  labelInput.setAttribute('aria-label', 'Filter label');
  const queryInput = document.createElement('input');
  queryInput.type = 'text';
  queryInput.placeholder = 'Query';
  queryInput.value = f.query || '';
  queryInput.className = 'settings-mini-input settings-mini-input-wide';
  queryInput.setAttribute('aria-label', 'Filter query');
  // Click is wired below (needs `mutate`, defined after the append).
  const del = removeButton({ ariaLabel: 'Remove filter' });
  editor.append(labelInput, queryInput, del);

  // Resolve the filter by id at action time (NOT a captured index), so a
  // retained editor stays correct after the list above it changes.
  const mutate = async (fn: (cur: any[], idx: number) => void) => {
    const cur = (Settings?.get('filters', []) || []).slice();
    const idx = cur.findIndex((x: any) => x.id === id);
    if (idx < 0) return;
    fn(cur, idx);
    try {
      await saveFilters(cur);
    } catch {
      // saveFilters → Settings.set rejects on a write failure; the store has
      // already rolled back + toasted. Swallow so this fire-and-forget change/
      // click handler can't leak an unhandled rejection. invalidate() below
      // still runs so the UI re-renders from the reverted persisted state.
    }
    ctx.invalidate();
  };
  labelInput.addEventListener('change', () =>
    mutate((cur, idx) => {
      cur[idx] = { ...cur[idx], label: labelInput.value.trim() };
    })
  );
  queryInput.addEventListener('change', () =>
    mutate((cur, idx) => {
      cur[idx] = { ...cur[idx], query: queryInput.value.trim() };
    })
  );
  del.addEventListener('click', () =>
    mutate((cur, idx) => {
      cur.splice(idx, 1);
    })
  );
  return editor;
}

/**
 * @param {any[]} rows - candidate { label, query } entries.
 * @returns {Promise<void>}
 */
async function saveFilters(rows: any) {
  if (!Settings) return;
  const out = [];
  const seen = new Set();
  for (const r of rows) {
    const id = r.id || crypto.randomUUID();
    const label = (r.label || '').trim();
    const query = (r.query || '').trim();
    if (!label) {
      out.push({ id, label: '', query });
      continue;
    }
    if (label.includes(':')) {
      out.push({ id, label: '', query });
      continue;
    }
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id, label, query });
  }
  await Settings.set('filters', out);
  const valid = out.filter((r) => r.label && r.query);
  FilterShortcuts?.setCustomFilters?.(valid);
}
